import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { userService, getErrorMessage } from '../../services/apiService';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import Icon from '../../components/AppIcon';
import UserTable from './components/UserTable';
import UserFormModal from './components/UserFormModal';
import UserFilters from './components/UserFilters';
import ResetPasswordModal from './components/ResetPasswordModal';
import ApprovalRoutingPanel from './components/ApprovalRoutingPanel';
import useApprovalRouting from './useApprovalRouting';
import { combosOfUser, groupCombos, routingChanges } from './routing';
import { useToast } from '../../components/ui/Toast';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { useConfirm } from '../../hooks/useConfirm';

// The page's three views, kept in the address (?tab=) so a link can open the right one.
const TABS = [
  { id: 'users', label: 'Users', icon: 'Users' },
  { id: 'invite', label: 'Invite approvers', icon: 'MailCheck', kind: 'INVITE' },
  { id: 'quality', label: 'Quality approvers', icon: 'ShieldCheck', kind: 'QUALITY' },
];
const SUBTITLE = {
  invite: 'Who approves a new vendor invite before the registration link goes out',
  quality: 'Who clears an RM or PM supplier after they submit the form, before Level 1',
};

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const toast = useToast();
  const { confirmState, confirm, setConfirmState } = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some((t) => t.id === searchParams.get('tab')) ? searchParams.get('tab') : 'users';
  const activeKind = TABS.find((t) => t.id === tab)?.kind;
  const routing = useApprovalRouting();
  const [addingCombo, setAddingCombo] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    status: '',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 1,
  });
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);

  const setTab = (id) => {
    setAddingCombo(false);
    setSearchParams(id === 'users' ? {} : { tab: id }, { replace: true });
  };

  // Tab counts and the table's "Approves" column come from the same rules.
  const comboCount = useMemo(
    () => ({ INVITE: groupCombos(routing.rules, 'INVITE').length, QUALITY: groupCombos(routing.rules, 'QUALITY').length }),
    [routing.rules]
  );
  const routingByUser = useMemo(() => {
    const out = {};
    for (const r of routing.rules) {
      if (r.approver_user_id != null && !out[r.approver_user_id]) out[r.approver_user_id] = combosOfUser(routing.rules, r.approver_user_id);
    }
    return out;
  }, [routing.rules]);

  // Fetch users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search,
        role: filters.role,
      };

      // Transform status filter to isActive boolean
      if (filters.status === 'active') {
        params.isActive = true;
      } else if (filters.status === 'inactive') {
        params.isActive = false;
      }

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await userService.getUsers(params);

      if (response.success) {
        setUsers(response.data);
        if (response.meta) {
          setPagination(prev => ({
            ...prev,
            total: response.meta.total,
            totalPages: response.meta.totalPages,
          }));
        }
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [pagination.page, pagination.limit, filters]);

  // Handle create user
  const handleCreate = () => {
    setSelectedUser(null);
    setModalMode('create');
    setShowModal(true);
  };

  // Handle edit user
  const handleEdit = (user) => {
    setSelectedUser(user);
    setModalMode('edit');
    setShowModal(true);
  };

  // Handle delete user
  const handleDelete = async (userId) => {
    const mine = combosOfUser(routing.rules, userId);
    const named = mine.INVITE.length + mine.QUALITY.length;
    const confirmed = await confirm({
      title: 'Deactivate User',
      message: named
        ? `Are you sure you want to deactivate this user? They approve ${named} combination${named === 1 ? '' : 's'}; while inactive they are skipped and the others in those combinations (or the administrators) approve instead.`
        : 'Are you sure you want to deactivate this user?',
      variant: 'warning',
      confirmText: 'Deactivate',
      cancelText: 'Cancel'
    });

    if (!confirmed) return;

    try {
      await userService.deleteUser(userId);
      fetchUsers();
      routing.reload();
      toast.success('User deactivated successfully');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  // Handle reset password
  const handleResetPassword = (user) => {
    setResetPasswordUser(user);
    setShowResetPasswordModal(true);
  };

  // Handle reset password submit
  const handleResetPasswordSubmit = async (newPassword) => {
    try {
      await userService.resetPassword(resetPasswordUser.id, newPassword);
      toast.success(`Password reset successfully for ${resetPasswordUser.full_name || resetPasswordUser.email}`);
      setShowResetPasswordModal(false);
    } catch (err) {
      throw err; // Let the modal handle the error
    }
  };

  // Handle form submit (create/edit), then the combinations ticked under "Approves"
  const handleFormSubmit = async (formData, approves) => {
    let saved;
    if (modalMode === 'create') {
      const res = await userService.createUser(formData);
      saved = res?.data;
    } else {
      await userService.updateUser(selectedUser.id, formData);
      saved = selectedUser;
    }
    setShowModal(false);
    fetchUsers();
    const name = formData.full_name || saved?.full_name || 'User';
    const changes = saved?.id && approves ? routingChanges(routing.rules, saved.id, approves) : { add: [], remove: [] };
    if (changes.add.length || changes.remove.length) {
      const failed = await routing.applyUserChanges(saved.id, changes);
      if (failed.length) {
        toast.error(`${name} was saved, but ${failed.length} approval change${failed.length === 1 ? '' : 's'} did not go through: ${failed[0].reason}`);
        return;
      }
    } else {
      routing.reload();
    }
    toast.success(modalMode === 'create' ? `${name} added` : `${name} updated`);
  };

  // Handle filter change
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle page change
  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const countFor = (t) => (t.kind ? comboCount[t.kind] : pagination.total);

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { label: 'Dashboard', path: '/dashboard-overview' },
          { label: 'User Management', path: '/user-management' }
        ]}
      />

      {/* Page Header: the action on the right follows the open tab */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
            User Management
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            {activeKind ? SUBTITLE[tab] : `Manage system users and their roles (${pagination.total} users)`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {activeKind ? (
            <>
              <Button variant="outline" onClick={handleCreate} iconName="UserPlus" iconPosition="left">
                Add User
              </Button>
              <Button onClick={() => setAddingCombo(true)} disabled={addingCombo} iconName="Plus" iconPosition="left" data-testid="new-combination">
                New combination
              </Button>
            </>
          ) : (
            <Button
              variant="default"
              onClick={handleCreate}
              iconName="UserPlus"
              iconPosition="left"
            >
              Add User
            </Button>
          )}
        </div>
      </div>

      {/* Tabs: users and the two approver lists, one click apart */}
      <div className="border-b border-slate-200 overflow-x-auto">
        <nav className="-mb-px flex gap-1" role="tablist" aria-label="User Management">
          {TABS.map((t) => {
            const active = t.id === tab;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-t-md ${
                  active ? 'border-primary text-primary' : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300'
                }`}
              >
                <Icon name={t.icon} size={16} />
                {t.label}
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${active ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-600'}`}>
                  {t.kind && routing.loading ? '…' : countFor(t)}
                </span>
              </button>
            );
          })}
        </nav>
      </div>

      {activeKind ? (
        <ApprovalRoutingPanel key={activeKind} kind={activeKind} routing={routing} adding={addingCombo} onAddingChange={setAddingCombo} confirm={confirm} />
      ) : (
        <>
          {/* Error Display */}
          {error && (
            <Card className="border border-red-200 bg-red-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Icon name="AlertCircle" size={20} className="text-red-600" />
                  <p className="text-red-800">{error}</p>
                  <Button variant="outline" size="sm" onClick={fetchUsers}>
                    Retry
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Filters */}
          <UserFilters
            filters={filters}
            onFilterChange={handleFilterChange}
          />

          {/* Users Table */}
          <Card className="border border-slate-200 shadow-sm bg-white">
            <CardContent className="p-0">
              {loading && users.length === 0 ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mx-auto"></div>
                    <p className="mt-4 text-slate-600">Loading users...</p>
                  </div>
                </div>
              ) : (
                <UserTable
                  users={users}
                  currentUserId={currentUser?.id}
                  loading={loading}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onResetPassword={handleResetPassword}
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  routingByUser={routingByUser}
                  onShowRouting={(kind) => setTab(kind === 'INVITE' ? 'invite' : 'quality')}
                />
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <UserFormModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSubmit={handleFormSubmit}
          user={selectedUser}
          mode={modalMode}
          routingRules={routing.rules}
        />
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && (
        <ResetPasswordModal
          isOpen={showResetPasswordModal}
          onClose={() => setShowResetPasswordModal(false)}
          onSubmit={handleResetPasswordSubmit}
          user={resetPasswordUser}
        />
      )}
      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmState.isOpen}
        onClose={confirmState.onCancel}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        variant={confirmState.variant}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
      />
    </div>
  );
};

export default UserManagement;
